import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useLanguage, LangCode, LANGUAGES } from "@/contexts/LanguageContext";
import { Globe, Search } from "lucide-react";
import { useState, useMemo } from "react";

export function LanguageBottomSheet() {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const handleSelect = (code: LangCode) => {
    setLang(code);
    setOpen(false);
    setSearch("");
  };

  const current = LANGUAGES.find((l) => l.code === lang);

  const filtered = useMemo(() => {
    if (!search.trim()) return LANGUAGES;
    const q = search.toLowerCase();
    return LANGUAGES.filter(
      (l) =>
        l.label.toLowerCase().includes(q) ||
        l.code.toLowerCase().includes(q)
    );
  }, [search]);

  return (
    <Sheet open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
      <SheetTrigger asChild>
        <button
          className="h-auto px-4 py-2 rounded-full glass-light flex items-center gap-2 hover:scale-105 transition-transform shadow-sm"
          aria-label="Change language"
        >
          <span className="text-sm font-semibold text-foreground indic-text">{current?.label}</span>
        </button>
      </SheetTrigger>
      <SheetContent side="bottom" className="rounded-t-3xl px-4 pb-8 pt-6 max-h-[75vh]">
        <div className="w-12 h-1.5 rounded-full bg-muted mx-auto mb-5" />
        <h3 className="font-display text-lg font-bold text-foreground text-center mb-1">
          भाषा चुनें • Choose Language
        </h3>
        <p className="text-xs text-muted-foreground text-center mb-4">
          Currently: {current?.label}
        </p>

        {/* Search */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search language..."
            className="w-full pl-9 pr-4 py-2.5 rounded-xl bg-muted/50 border border-border text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30"
          />
        </div>

        {/* Language Grid */}
        <div className="grid grid-cols-3 gap-2.5 overflow-y-auto max-h-[45vh] pr-1">
          {filtered.map((l) => (
            <button
              key={l.code}
              onClick={() => handleSelect(l.code as LangCode)}
              className={`rounded-xl p-3 text-center transition-all duration-200 border-2 ${
                lang === l.code
                  ? "border-primary bg-primary/10 shadow-sm"
                  : "border-transparent bg-muted/50 hover:bg-muted hover:border-border"
              }`}
            >
              <span className="block text-sm font-bold text-foreground leading-tight indic-text">
                {l.label}
              </span>
              <span className="block text-[10px] text-muted-foreground mt-0.5">
                {l.code.toUpperCase()}
              </span>
            </button>
          ))}
          {filtered.length === 0 && (
            <p className="col-span-3 text-center text-sm text-muted-foreground py-4">
              No language found
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
